import {
  RawGDORow,
  ValidatedGDORow,
  ValidationRefData,
  ColumnKey,
  COLUMN_KEYS,
  COLUMN_HEADERS,
} from './GDOBulkUploadTypes';
import { IBP_TEMPLATE, ColumnDefinition, ValidationRule } from './ibp-template';

/**
 * Generate a deterministic Job_Id for a row, mirroring the backend's
 * bulkUpdateEntities.js generateJobId() function.
 *
 * Uses the Web Crypto API (SHA-256) for platforms that need hashed IDs.
 * This must stay in sync with Backend/server/service/v2/bulkUpdateEntities.js
 */
async function generateJobId(row: RawGDORow): Promise<string> {
  const platform = (row.platform || '').toLowerCase().trim();
  const scheduler = (row.schedulerName || '').toLowerCase().trim();
  const jobName = (row.jobName || '').trim();

  if (!jobName) return '';

  // SHA-256 helper using Web Crypto API
  const sha256 = async (input: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  switch (platform) {
    case 'adf':
      // AddKind.js: sha256(pipelineId) — we don't have pipelineId, so hash jobName
      return sha256(jobName);

    case 'adb':
      // AddKindADB.js: job.job_id from ES (numeric, no hashing).
      // We don't have the numeric job_id, so use jobName directly.
      return jobName;

    case 'sql server agent':
      // AddKindSqlAgentJob.js: sqlAgent.job_id from ES.
      // Use jobName directly.
      return jobName;

    case 'bods':
      // AddKindControlM.js:
      //   HWA → jobName directly
      //   Control-M-PGT → ${APPLIC}_${SCHEDTAB}_${jobName}
      if (scheduler === 'hwa') {
        return jobName;
      }
      // Control-M-PGT: Use application/folderPath from Excel if provided
      // Falls back to jobName (matches HWA pattern)
      const applicBods = (row as any).application?.trim() || '';
      const schedTabBods = (row as any).folderPath?.trim() || '';
      if (applicBods && schedTabBods) {
        return `${applicBods}_${schedTabBods}_${jobName}`;
      }
      return jobName;

    case 'teradata':
      // AddKindControlM.js:
      //   HWA → jobName directly
      //   Control-M → ${APPLIC}_${SCHEDTAB}_${jobName}
      if (scheduler === 'hwa') {
        return jobName;
      }
      // Control-M: Use application/folderPath from Excel if provided
      // Falls back to jobName (matches HWA pattern)
      const applicTd = (row as any).application?.trim() || '';
      const schedTabTd = (row as any).folderPath?.trim() || '';
      if (applicTd && schedTabTd) {
        return `${applicTd}_${schedTabTd}_${jobName}`;
      }
      return jobName;

    case 'ddh':
      return jobName;

    case 'mdt':
      return jobName;

    case 'sap bw':
      return jobName;

    case 'windows server':
      return jobName;

    default:
      return jobName;
  }
}

/**
 * Platform-Scheduler Mapping (loaded from backend via refData.platformSchedulerMap)
 * 
 * This is the single source of truth. The mapping is defined in:
 * Backend/server/service/v2/platformSchedulerMap.js
 * and fetched via the bulk upload lookups API.
 */

const ALLOWED_CHARS_REGEX = /^[a-zA-Z0-9 !@#$%^&*()_+=\-|{}":?><\]\[;'\\/.,]*$/;
const GDO_PREFIX_REGEX = /^GDO-[a-zA-Z0-9]+/;
const TIME_24HR_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAYS_REGEX = /^(All Days|(Mon|Tue|Wed|Thu|Fri|Sat|Sun)(,(Mon|Tue|Wed|Thu|Fri|Sat|Sun))*)$/;
const VALID_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SCHEDULE_INFO_REGEX =
  /^((All|Mon|Tue|Wed|Thu|Fri|Sat|Sun)(,(Mon|Tue|Wed|Thu|Fri|Sat|Sun))*)\/([01]\d|2[0-3]):[0-5]\d\/\d+\/(Daily|Weekly|Monthly|Bi-Weekly|Quarterly)$/;

const INBOUND_OUTBOUND_VALUES = ['Inbound', 'Outbound', 'Both'];

/**
 * Convert an Excel serial time value (fraction of a day, 0-1) to an HH:MM string.
 * Excel stores times as decimals: 0.291667 = 07:00, 0.5 = 12:00, 0.75 = 18:00.
 * Returns the original value unchanged if it's not a valid Excel time number.
 */
function excelTimeToHHMM(value: any): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value !== 'number' || isNaN(value)) return String(value);
  // Excel serial time is a fraction of a day in range [0, 1)
  if (value < 0 || value >= 1) return String(value);
  const totalMinutes = Math.round(value * 24 * 60);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export async function validateAllRows(
  rawRows: RawGDORow[],
  refData: ValidationRefData
): Promise<ValidatedGDORow[]> {
  // Create composite key for duplicate detection: GDO Name + Sector + Market + Report + Origin + Destination + Job Name + Scheduler Name (+ Trigger Name for ADF)
  // Map composite key -> array of row indices
  const fileCompositeKeyIndices = new Map<string, number[]>();
  rawRows.forEach((r, idx) => {
    const isADF = (r.platform || '').trim().toUpperCase() === 'ADF';
    const triggerPart = isADF ? (r.triggerName || '').trim().toLowerCase() : '';
    const compositeKey = [
      r.gdoName.trim().toLowerCase(),
      r.sector.trim().toLowerCase(),
      r.market.trim().toLowerCase(),
      r.report.trim().toLowerCase(),
      r.origin.trim().toLowerCase(),
      r.destination.trim().toLowerCase(),
      r.jobName.trim().toLowerCase(),
      r.schedulerName.trim().toLowerCase(),
      triggerPart,
    ].join('|');
    const indices = fileCompositeKeyIndices.get(compositeKey) || [];
    indices.push(idx);
    fileCompositeKeyIndices.set(compositeKey, indices);
  });

  // Cross-batch validation: collect all GDO names defined in this batch per market
  // Maps market -> Set of GDO names in this batch
  const batchGDOsByMarket = new Map<string, Set<string>>();
  rawRows.forEach((r) => {
    const market = r.market.trim().toUpperCase();
    if (!batchGDOsByMarket.has(market)) {
      batchGDOsByMarket.set(market, new Set());
    }
    batchGDOsByMarket.get(market)!.add(r.gdoName.trim());
  });

  // Pre-compute composite keys for all rows to detect duplicates within the sheet
  // Composite key: GDO + Market + Origin + Destination + Platform + Scheduler + Job Name (+ Trigger Name for ADF)
  // Maps composite key -> array of row indices
  const compositeKeyIndices = new Map<string, number[]>();
  for (let idx = 0; idx < rawRows.length; idx++) {
    const r = rawRows[idx];
    const isADF = (r.platform || '').trim().toUpperCase() === 'ADF';
    const triggerPart = isADF ? (r.triggerName || '').trim().toUpperCase() : '';
    const compositeKey = [
      r.gdoName.trim().toUpperCase(),
      r.market.trim().toUpperCase(),
      r.origin.trim().toUpperCase(),
      r.destination.trim().toUpperCase(),
      r.platform.trim().toUpperCase(),
      r.schedulerName.trim().toUpperCase(),
      r.jobName.trim().toUpperCase(),
      triggerPart,
    ].join('|');
    if (r.jobName.trim()) {
      const indices = compositeKeyIndices.get(compositeKey) || [];
      indices.push(idx);
      compositeKeyIndices.set(compositeKey, indices);
    }
  }

  return rawRows.map((row, index) => {
    const cellErrors: Record<ColumnKey, string> = {} as Record<ColumnKey, string>;
    let rowError: string | undefined;

    // Use template-based validation for each column
    for (const column of IBP_TEMPLATE.columns) {
      const columnKey = column.key as ColumnKey;
      const value = row[columnKey as keyof RawGDORow];
      const stringValue = String(value || '').trim();

      // Skip validation for remarks column (system-generated)
      if (columnKey === 'remarks') continue;

      // Validate required fields
      if (column.required && !stringValue) {
        setError(cellErrors, columnKey, 'Field is required');
        continue;
      }

      // Apply validation rules from template
      for (const rule of column.validation) {
        const error = applyValidationRule(stringValue, rule, row, refData, index, fileCompositeKeyIndices, batchGDOsByMarket);
        if (error) {
          setError(cellErrors, columnKey, error);
          break; // Stop on first error for this column
        }
      }
    }

    // Composite key duplicate validation: check within the sheet
    // Uniqueness = GDO + Market + Origin + Destination + Platform + Scheduler + Job Name (+ Trigger Name for ADF)
    if (row.jobName.trim()) {
      const isADF = (row.platform || '').trim().toUpperCase() === 'ADF';
      const triggerPart = isADF ? (row.triggerName || '').trim().toUpperCase() : '';
      const compositeKey = [
        row.gdoName.trim().toUpperCase(),
        row.market.trim().toUpperCase(),
        row.origin.trim().toUpperCase(),
        row.destination.trim().toUpperCase(),
        row.platform.trim().toUpperCase(),
        row.schedulerName.trim().toUpperCase(),
        row.jobName.trim().toUpperCase(),
        triggerPart,
      ].join('|');
      const duplicateIndices = compositeKeyIndices.get(compositeKey) || [];
      if (duplicateIndices.length > 1) {
        const otherRows = duplicateIndices.filter((i) => i !== index);
        const otherRowNums = otherRows.map((i) => rawRows[i].id).join(', ');
        const uniquenessFields = isADF
          ? 'same GDO, Market, Origin, Destination, Platform, Scheduler, Job Name, and Trigger Name'
          : 'same GDO, Market, Origin, Destination, Platform, Scheduler, and Job Name';
        let duplicateMsg = '';
        if (otherRows.length === 1) {
          duplicateMsg = `Duplicate entry with row ${otherRowNums} (${uniquenessFields})`;
        } else {
          duplicateMsg = `Duplicate entry with rows ${otherRowNums} (${uniquenessFields})`;
        }
        setError(cellErrors, 'jobName' as ColumnKey, duplicateMsg);
      }
    }

    const isValid = Object.keys(cellErrors).length === 0 && !rowError;

    // Populate remarks only for invalid rows
    let remarks = row.remarks || '';
    if (!isValid) {
      const errorMessages = Object.values(cellErrors).filter(Boolean);
      if (rowError) errorMessages.push(rowError);
      remarks = errorMessages.join('\n');
    }

    return { ...row, isValid, cellErrors, rowError, remarks };
  });
}

/**
 * Apply a validation rule from the template configuration
 */
function applyValidationRule(
  value: string,
  rule: ValidationRule,
  row: RawGDORow,
  refData: ValidationRefData,
  index: number,
  fileCompositeKeyIndices: Map<string, number[]>,
  batchGDOsByMarket: Map<string, Set<string>>
): string | null {
  // Skip validation for empty optional fields, EXCEPT for custom validators
  // that may need to check conditional required logic (e.g., application/folderPath
  // required when scheduler is Control-M)
  if (!value && rule.type !== 'required' && rule.type !== 'custom') {
    return null; // Skip validation for empty optional fields
  }

  switch (rule.type) {
    case 'required':
      return !value ? rule.message : null;

    case 'pattern':
      return rule.pattern && !rule.pattern.test(value) ? rule.message : null;

    case 'enum':
      if (!rule.allowedValues) return null;
      const allowed = Array.isArray(rule.allowedValues) 
        ? rule.allowedValues 
        : rule.allowedValues(refData);
      return !allowed.includes(value) ? rule.message : null;

    case 'reference':
      if (!rule.referenceType) return null;
      const refValues = refData[rule.referenceType];
      if (!refValues) return null;
      // Handle both string[] and Set<string>
      const lowerValue = value.toLowerCase();
      const refArray = Array.isArray(refValues) ? refValues : Array.from(refValues);
      const found = refArray.some((v: string) => v.toLowerCase() === lowerValue);
      if (found) return null;
      // Build a message that includes the actual valid options
      const validOptions = refArray.length > 0 ? refArray.join(', ') : 'none available';
      return `Invalid ${rule.referenceType.slice(0, -1)} "${value}". Valid options: ${validOptions}`;

    case 'custom':
      return rule.customValidator ? rule.customValidator(value, row as Record<string, any>, refData) : null;

    case 'dependent':
      // Handle dependent validation (duplicate detection)
      if (rule.dependsOn && rule.dependsOn.includes('sector')) {
        return validateDependentField(value, row, index, fileCompositeKeyIndices, refData.existingGDONames);
      }
      return null;

    default:
      return null;
  }
}

/**
 * Validate dependent fields (for duplicate detection)
 */
function validateDependentField(
  value: string,
  row: RawGDORow,
  index: number,
  fileCompositeKeyIndices: Map<string, number[]>,
  existingGDONames: Set<string>
): string | null {
  // Build composite key for duplicate check (+ Trigger Name for ADF)
  const isADF = (row.platform || '').trim().toUpperCase() === 'ADF';
  const triggerPart = isADF ? (row.triggerName || '').trim().toLowerCase() : '';
  const compositeKey = [
    row.gdoName.trim().toLowerCase(),
    row.sector.trim().toLowerCase(),
    row.market.trim().toLowerCase(),
    row.report.trim().toLowerCase(),
    row.origin.trim().toLowerCase(),
    row.destination.trim().toLowerCase(),
    row.jobName.trim().toLowerCase(),
    row.schedulerName.trim().toLowerCase(),
    triggerPart,
  ].join('|');

  const fileIndices = fileCompositeKeyIndices.get(compositeKey) || [];
  const inLiveSystem = existingGDONames.has(value.toLowerCase());

  if (fileIndices.length > 1 || inLiveSystem) {
    let duplicateMsg = 'Duplicate GDO Name';
    if (fileIndices.length > 1) {
      const otherRows = fileIndices.filter((i) => i !== index);
      if (otherRows.length === 1) {
        duplicateMsg = `Duplicate with row ${otherRows[0] + 1}`;
      } else if (otherRows.length > 1) {
        duplicateMsg = `Duplicate with rows ${otherRows.map((i) => i + 1).join(', ')}`;
      }
    }
    if (inLiveSystem) {
      duplicateMsg += duplicateMsg === 'Duplicate GDO Name' ? ' (exists in system)' : ' (exists in system)';
    }
    return duplicateMsg;
  }

  return null;
}

function setError(cellErrors: Record<ColumnKey, string>, key: ColumnKey, message: string) {
  if (!cellErrors[key]) {
    // Prepend the column header name to the error message
    const headerName = COLUMN_HEADERS[key] || key;
    cellErrors[key] = `${headerName}: ${message}`;
  }
}

function validateGDOName(
  row: RawGDORow,
  index: number,
  cellErrors: Record<ColumnKey, string>,
  fileCompositeKeyIndices: Map<string, number[]>,
  existingGDONames: Set<string>
) {
  const key: ColumnKey = 'gdoName';
  const value = row.gdoName.trim();
  const lower = value.toLowerCase();

  if (!value) {
    setError(cellErrors, key, 'Field is required');
    return;
  }

  // Build composite key for duplicate check (+ Trigger Name for ADF)
  const isADF = (row.platform || '').trim().toUpperCase() === 'ADF';
  const triggerPart = isADF ? (row.triggerName || '').trim().toLowerCase() : '';
  const compositeKey = [
    row.gdoName.trim().toLowerCase(),
    row.sector.trim().toLowerCase(),
    row.market.trim().toLowerCase(),
    row.report.trim().toLowerCase(),
    row.origin.trim().toLowerCase(),
    row.destination.trim().toLowerCase(),
    row.jobName.trim().toLowerCase(),
    row.schedulerName.trim().toLowerCase(),
    triggerPart,
  ].join('|');

  const fileIndices = fileCompositeKeyIndices.get(compositeKey) || [];
  const inLiveSystem = existingGDONames.has(lower);

  if (fileIndices.length > 1 || inLiveSystem) {
    // Build specific duplicate message
    let duplicateMsg = 'Duplicate GDO Name';
    if (fileIndices.length > 1) {
      // Find other rows with same composite key (excluding current row)
      const otherRows = fileIndices.filter((i) => i !== index);
      if (otherRows.length === 1) {
        duplicateMsg = `Duplicate with row ${otherRows[0] + 1}`;
      } else if (otherRows.length > 1) {
        duplicateMsg = `Duplicate with rows ${otherRows.map((i) => i + 1).join(', ')}`;
      }
    }
    if (inLiveSystem) {
      duplicateMsg += duplicateMsg === 'Duplicate GDO Name' ? ' (exists in system)' : ' (exists in system)';
    }
    setError(cellErrors, key, duplicateMsg);
    return;
  }

  if (!ALLOWED_CHARS_REGEX.test(value)) {
    setError(
      cellErrors,
      key,
      'We accept only these special characters: a-zA-Z0-9 !@#$%^&*()_+=-|}{":?><][;\'\\/.,'
    );
    return;
  }

  if (!GDO_PREFIX_REGEX.test(value)) {
    setError(cellErrors, key, 'GDO name should start with GDO-<alphanumeric>');
    return;
  }

  if (value.length > 64) {
    setError(cellErrors, key, `GDO name is too long (${value.length} chars). Maximum allowed: 64 characters`);
    return;
  }
}

function validateSector(
  row: RawGDORow,
  cellErrors: Record<ColumnKey, string>,
  sectors: string[]
) {
  const key: ColumnKey = 'sector';
  const value = row.sector.trim();

  if (!value) {
    setError(cellErrors, key, 'Field is required');
    return;
  }

  // Case-insensitive check
  const lower = value.toLowerCase();
  const found = sectors.some((s) => s.toLowerCase() === lower);
  if (!found) {
    const options = sectors.length > 0 ? sectors.join(', ') : 'none loaded';
    setError(cellErrors, key, `Invalid sector "${value}". Valid options: ${options}`);
  }
}

function validateMarket(
  row: RawGDORow,
  cellErrors: Record<ColumnKey, string>,
  markets: string[]
) {
  const key: ColumnKey = 'market';
  const value = row.market.trim();

  if (!value) {
    setError(cellErrors, key, 'Field is required');
    return;
  }

  // Case-insensitive check
  const lower = value.toLowerCase();
  const found = markets.some((m) => m.toLowerCase() === lower);
  if (!found) {
    const options = markets.length > 0 ? markets.join(', ') : 'none loaded';
    setError(cellErrors, key, `Invalid market "${value}". Valid options: ${options}`);
  }
}

function validateReport(
  row: RawGDORow,
  cellErrors: Record<ColumnKey, string>,
  reports: string[]
) {
  const key: ColumnKey = 'report';
  const value = row.report.trim();

  if (!value) {
    setError(cellErrors, key, 'Field is required');
    return;
  }

  // Case-insensitive check
  const lower = value.toLowerCase();
  const found = reports.some((r) => r.toLowerCase() === lower);
  if (!found) {
    const options = reports.length > 0 ? reports.join(', ') : 'none loaded';
    setError(cellErrors, key, `Invalid report "${value}". Valid options: ${options}`);
  }
}

function validateOrigin(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'origin';
  const value = row.origin.trim();
  if (!value) {
    setError(cellErrors, key, 'Field is required');
  }
}

function validateDestination(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'destination';
  const value = row.destination.trim();
  if (!value) {
    setError(cellErrors, key, 'Field is required');
  }
}

function validateTimezone(
  row: RawGDORow,
  cellErrors: Record<ColumnKey, string>
) {
  const key: ColumnKey = 'timezone';
  const value = row.timezone.trim();

  if (!value) {
    setError(cellErrors, key, 'Field is required');
    return;
  }

  // Timezone validation — accept all standard formats:
  // 1. Abbreviations: 2-5 uppercase letters (UTC, GMT, EST, EDT, CST, CDT, CET, IST, AEST, JST, etc.)
  // 2. UTC offsets: UTC±N or UTC±N:MM (e.g., UTC-5, UTC+5:30, UTC+14)
  // 3. IANA names: Area/Location (e.g., America/New_York, Europe/London, Asia/Tokyo)
  const TIMEZONE_REGEX = /^(UTC[+-]?\d{1,2}(:\d{2})?|[A-Z]{2,5}|[A-Za-z]+\/[A-Za-z_]+)$/;

  if (!TIMEZONE_REGEX.test(value)) {
    setError(cellErrors, key, `Invalid timezone "${value}". Examples: CST, EST, CET, IST, UTC, UTC-5, America/New_York, Europe/London`);
    return;
  }
}

function validateOwner(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'owner';
  const value = row.owner.trim();

  if (!value) {
    setError(cellErrors, key, 'Field is required');
  }
}

function validateGDODescription(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  // Optional field - no validation required
  // Empty is valid
}

/**
 * Validate GDO Dependent On field with cross-batch validation.
 * Dependencies are scoped by market — a GDO can only depend on GDOs in the same market.
 * Checks: format (comma-separated GDO names), no self-dependency, GDO- prefix,
 * and that referenced GDOs exist either in the system or in the current batch (same market).
 */
function validateGDODependentOn(
  row: RawGDORow,
  cellErrors: Record<ColumnKey, string>,
  existingGDONames: Set<string>,
  batchGDOsByMarket: Map<string, Set<string>>
) {
  const key: ColumnKey = 'gdoDependentOn';
  const value = row.gdoDependentOn.trim();

  // Optional field — empty is valid
  if (!value) return;

  // Special value 'NONE' clears all dependencies for this GDO
  if (value.toUpperCase() === 'NONE') return;

  const market = row.market.trim().toUpperCase();
  const batchGDONames = batchGDOsByMarket.get(market) || new Set<string>();

  // Split by comma and validate each dependency GDO name
  const depNames = value.split(',').map((s) => s.trim()).filter(Boolean);

  for (const dep of depNames) {
    // Must start with GDO- prefix
    if (!dep.startsWith('GDO-')) {
      setError(cellErrors, key, `Dependency "${dep}" must start with GDO-`);
      return;
    }
    // Must not be the same as this GDO
    if (dep.toLowerCase() === row.gdoName.trim().toLowerCase()) {
      setError(cellErrors, key, `GDO cannot depend on itself`);
      return;
    }
    // Must exist in the system OR in the current batch (same market)
    const inSystem = existingGDONames.has(dep.toLowerCase());
    const inBatch = batchGDONames.has(dep);
    if (!inSystem && !inBatch) {
      setError(cellErrors, key, `Dependency "${dep}" not found in system or current batch (market: ${market})`);
      return;
    }
  }
}

function validateStageInboundOutbound(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'stageInboundOutbound';
  const value = row.stageInboundOutbound.trim();

  if (value && !INBOUND_OUTBOUND_VALUES.includes(value)) {
    setError(cellErrors, key, `Invalid value "${value}". Valid options: ${INBOUND_OUTBOUND_VALUES.join(', ')}`);
  }
}

function validateDataOwner(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  // Optional field - no validation required
}

function validateDataSteward(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  // Optional field - no validation required
}

// Platform and scheduler validation is handled via custom validators in ibp-template.ts
// using refData.platformSchedulerMap fetched from the backend API.
// No standalone validation functions needed here.

function validateJobName(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'jobName';
  const value = row.jobName.trim();
  if (!value) {
    setError(cellErrors, key, 'Field is required');
  }
}

function validateScheduleDays(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'scheduleDays';
  const value = row.scheduleDays.trim();

  if (!value) {
    setError(cellErrors, key, 'Field is required');
    return;
  }

  const validDays = ['all', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const days = value.split(',').map(d => d.trim().toLowerCase());
  for (const day of days) {
    if (!validDays.includes(day)) {
      setError(cellErrors, key, `Invalid day "${day}". Use full names: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday, or All.`);
      return;
    }
  }
}

function validateExecutionStartTime(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'executionStartTime';
  const value = row.executionStartTime.trim();

  if (!value) {
    setError(cellErrors, key, 'Field is required');
    return;
  }

  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
    setError(cellErrors, key, `Invalid time format "${value}". Expected HH:MM in 24-hour format (e.g., 06:00, 14:30)`);
  }
}

function validateExecutionEndTime(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'executionEndTime';
  const value = row.executionEndTime.trim();

  // Optional field
  if (!value) return;

  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
    setError(cellErrors, key, `Invalid time format "${value}". Expected HH:MM in 24-hour format (e.g., 08:00, 16:30)`);
  }
}

function validateSLAType(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  // Optional field — no list-based validation
}

function validateSLADays(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'slaDays';
  const value = row.slaDays.trim();

  // Optional field
  if (!value) return;

  if (!DAYS_REGEX.test(value)) {
    setError(cellErrors, key, `Invalid value "${value}". Use "All Days" or comma-separated days: Mon,Tue,Wed,Thu,Fri,Sat,Sun`);
    return;
  }

  if (value !== 'All Days') {
    const days = value.split(',');
    const uniqueDays = new Set(days);
    if (days.length !== uniqueDays.size) {
      setError(cellErrors, key, 'Duplicate days not allowed. Each day should appear only once');
    }
  }
}

function validateSLAStartTime(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'slaStartTime';
  const value = row.slaStartTime.trim();

  // Optional field
  if (!value) return;

  // Single time or comma-separated list of times (multiple SLA slots/day)
  const entries = value.split(',').map(v => v.trim()).filter(Boolean);
  const invalid = entries.filter(v => !TIME_24HR_REGEX.test(v));
  if (!entries.length || invalid.length) {
    setError(
      cellErrors,
      key,
      `Invalid time "${invalid[0] || value}". Expected 24-hr format HH:MM, comma-separated for multiple SLA slots (e.g., 08:00 or 06:00,14:00)`
    );
  }
}

function validateSLAEndTime(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'slaEndTime';
  const value = row.slaEndTime.trim();

  // Optional field
  if (!value) return;

  // Single time or comma-separated list of times (multiple SLA slots/day)
  const entries = value.split(',').map(v => v.trim()).filter(Boolean);
  const invalid = entries.filter(v => !TIME_24HR_REGEX.test(v));
  if (!entries.length || invalid.length) {
    setError(
      cellErrors,
      key,
      `Invalid time "${invalid[0] || value}". Expected 24-hr format HH:MM, comma-separated for multiple SLA slots (e.g., 17:00 or 10:00,18:00)`
    );
    return;
  }

  const startEntries = row.slaStartTime.trim().split(',').map(v => v.trim()).filter(Boolean);
  const endDayOffset = parseInt(row.slaEndDayOffset?.trim() || '0', 10) || 0;

  // Pair slots by position (a single start/end is reused for every slot) and
  // allow end before start only when endDayOffset > 0 (overnight deadline).
  const slotCount = Math.max(startEntries.length, entries.length);
  for (let i = 0; i < slotCount; i++) {
    const start =
      startEntries[i] ?? (startEntries.length === 1 ? startEntries[0] : undefined);
    const end = entries[i] ?? (entries.length === 1 ? entries[0] : undefined);
    if (!start || !end || !TIME_24HR_REGEX.test(start)) continue;

    if (end <= start && endDayOffset === 0) {
      const slotLabel = slotCount > 1 ? ` (slot ${i + 1})` : '';
      setError(
        cellErrors,
        key,
        `End time "${end}"${slotLabel} must be after start time "${start}". Set SLA End Day Offset to 1 for overnight deadlines.`
      );
      return;
    }
  }
}

const SLA_CRITERIA_VALUES = ['ANY RUN', 'ALL RUNS', 'SELECTED RUNS', 'LATEST RUN'];

function validateSLACriteria(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'slaCriteria';
  const value = row.slaCriteria.trim();

  // Optional field
  if (!value) return;

  if (!SLA_CRITERIA_VALUES.includes(value.toUpperCase())) {
    setError(
      cellErrors,
      key,
      `Invalid SLA Criteria "${value}". Valid options: ${SLA_CRITERIA_VALUES.join(', ')}`
    );
  }
}

function validateSLAEndDayOffset(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'slaEndDayOffset';
  const value = row.slaEndDayOffset?.trim();

  // Optional field
  if (!value) return;

  const num = parseInt(value, 10);
  if (isNaN(num) || num < 0 || num > 2) {
    setError(cellErrors, key, `Invalid value "${value}". Expected: 0 (same day), 1 (next day), or 2 (2 days later)`);
  }
}

function validateSLAMaxRuntimeMinutes(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'slaMaxRuntimeMinutes';
  const value = row.slaMaxRuntimeMinutes?.trim();

  // Optional field
  if (!value) return;

  // Single value or comma-separated list (one value per SLA slot)
  const entries = value.split(',').map(v => v.trim()).filter(Boolean);
  const invalid = entries.filter(v => {
    const num = parseInt(v, 10);
    return isNaN(num) || num <= 0 || String(num) !== v;
  });
  if (!entries.length || invalid.length) {
    setError(
      cellErrors,
      key,
      `Invalid value "${invalid[0] || value}". Must be positive integers, comma-separated for multiple SLA slots (e.g., 60 or 30,45,60)`
    );
  }
}

function validateSLAScheduleTrigger(row: RawGDORow, cellErrors: Record<ColumnKey, string>) {
  const key: ColumnKey = 'slaScheduleTrigger';
  const value = row.slaScheduleTrigger?.trim();

  // Optional field
  if (!value) return;

  const allowedValues = ['Scheduled', 'Triggered'];
  if (!allowedValues.includes(value)) {
    setError(cellErrors, key, `Invalid value "${value}". Valid options: ${allowedValues.join(', ')}`);
  }
}

/**
 * Strip BOM (Byte Order Mark) from the start of a string.
 * Excel/CSV exports sometimes include \uFEFF at the beginning.
 */
function stripBOM(s: string): string {
  return s?.replace(/^\uFEFF/, '') ?? s;
}

export function validateFileHeaders(headers: string[]): string | null {
  // Exclude 'remarks' from expected headers — the template never includes it
  // (remarks is system-populated, only shown in error/invalid entries sheets)
  const expectedHeaders = COLUMN_KEYS.filter((k) => k !== 'remarks').map((k) => COLUMN_HEADERS[k]);

  // Strip BOM from all headers
  const cleanHeaders = headers.map((h) => stripBOM(h ?? ''));

  // Trim trailing empty/undefined columns — Excel sometimes adds extra empty
  // columns when the user opens and re-saves the file.
  let trimmed = [...cleanHeaders];
  while (trimmed.length > 0 && (trimmed[trimmed.length - 1]?.trim() ?? '') === '') {
    trimmed.pop();
  }

  // Handle two possible header formats:
  // 1. Standard template: [79 user columns] (without Remarks, as Remarks is system-populated)
  // 2. Invalid entries template: [Remarks column] + [79 user columns]
  //    This happens when users download the invalid entries template and re-upload it

  // Check if this is the invalid entries template format (has Remarks as first column)
  const hasRemarksFirst = trimmed.length > 0 && trimmed[0]?.trim() === COLUMN_HEADERS['remarks'];

  // Determine the actual user columns to validate
  let userHeaders = trimmed;

  // If Remarks is first, it's not part of the user columns we need to validate
  // (it's system-generated and will be ignored during parsing)
  if (hasRemarksFirst) {
    userHeaders = userHeaders.slice(1);
  }

  // Now validate against the expected user columns
  if (userHeaders.length < expectedHeaders.length) {
    const found = userHeaders.length;
    const need = expectedHeaders.length;
    return `File is missing ${need - found} column(s). Expected ${need} columns but found ${found}. Please download the latest template and retry.`;
  }

  if (userHeaders.length > expectedHeaders.length) {
    return `File has ${userHeaders.length} user columns but expected ${expectedHeaders.length}. Please download the latest template without modifying columns.`;
  }

  for (let i = 0; i < expectedHeaders.length; i++) {
    const actual = userHeaders[i]?.trim();
    if (actual !== expectedHeaders[i]) {
      return `Column ${i + 1} header mismatch: found "${actual}" but expected "${expectedHeaders[i]}". Please download the latest template and retry.`;
    }
  }
  return null;
}

/**
 * Smartly normalize a GDO name input to the canonical GDO-<id> format.
 *
 * Rules:
 *  1. "GDO 001", "gdo A1B", "gdo-001" → "GDO-001", "GDO-A1B", "GDO-001"
 *     If it starts with GDO/gdo (optionally followed by '-'), then a space,
 *     and then alphanumeric characters, collapse to GDO-<alphanumeric>.
 *  2. "001", "123" (just a number) → "GDO-001", "GDO-123"
 *  3. Already correct format like "GDO-001" → returned as-is.
 *  4. Anything else → returned as-is (validation will flag invalid format).
 *
 * @param raw - Raw user input for the GDO name
 * @returns Normalized GDO name in canonical GDO-<id> format
 */
export function normalizeGdoName(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  // Already in correct format: GDO-<something>
  if (/^GDO-[a-zA-Z0-9]+$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  // Rule 1: "GDO 001", "gdo A1B", "gdo-001" → "GDO-001", "GDO-A1B"
  // Matches: GDO/gdo, optional dash or space, then alphanumeric sequence
  const gdoSpaceMatch = trimmed.match(/^GDO[\s\-]+([a-zA-Z0-9]+)$/i);
  if (gdoSpaceMatch) {
    return `GDO-${gdoSpaceMatch[1].toUpperCase()}`;
  }

  // Rule 2: Just a number → "GDO-<number>"
  const numericMatch = trimmed.match(/^(\d+)$/);
  if (numericMatch) {
    return `GDO-${numericMatch[1]}`;
  }

  // Return as-is — validation will flag if it doesn't match GDO-<id> pattern
  return trimmed;
}

/**
 * Normalize all GDO names in a comma-separated dependency string.
 * Each token goes through the same normalizeGdoName logic.
 */
function normalizeGdoDependentOn(raw: string): string {
  if (!raw) return '';
  return raw
    .split(',')
    .map((token) => normalizeGdoName(token))
    .join(',');
}

export function normalizeParsedRows(parsedRows: Record<string, any>[]): RawGDORow[] {
  // Filter out completely empty rows (all cells cleared/deleted by user)
  const nonEmptyRows = parsedRows.filter((row) => {
    const hasAnyValue = Object.values(row).some((v) => {
      const s = String(v ?? '').trim();
      return s !== '';
    });
    return hasAnyValue;
  });

  return nonEmptyRows.map((row, index) => {
    // Ignore the remarks column from user input - it's system-generated
    // The remarks field will be populated by validation logic
    const remarksFromUser = String(row['Remarks / Error Flag'] || '').trim();
    const { 'Remarks / Error Flag': _remarksIgnored, ...userInputRow } = row;

    return {
    id: index + 1,
    gdoName: normalizeGdoName(String(userInputRow['GDO Name *'] || userInputRow['GDO Name'] || '')),
    sector: String(userInputRow['Sector *'] || userInputRow['Sector'] || '').trim().toUpperCase(),
    market: String(userInputRow['Market / Country *'] || userInputRow['Market *'] || userInputRow['Market'] || '').trim().toUpperCase(),
    report: String(userInputRow['Report *'] || userInputRow['Report'] || '').trim().toUpperCase(),
    origin: String(userInputRow['Origin *'] || userInputRow['Origin'] || '').trim(),
    destination: String(userInputRow['Destination *'] || userInputRow['Destination'] || '').trim(),
    timezone: String(userInputRow['Timezone *'] || userInputRow['Timezone'] || '').trim(),
    owner: String(userInputRow['Owner *'] || userInputRow['Owner'] || '').trim(),
    gdoDescription: String(userInputRow['GDO Description/Label'] || userInputRow['GDO Description'] || '').trim(),
    gdoCategory: String(userInputRow['GDO Category'] || '').trim(),
    gdoDependentOn: normalizeGdoDependentOn(String(userInputRow['GDO Dependent On'] || '')),
    stageInboundOutbound: String(userInputRow['Stage Inbound/Outbound to o9'] || '').trim(),
    gdoStep: String(userInputRow['GDO step'] || '').trim(),
    dataOwner: String(userInputRow['Data Owner'] || '').trim(),
    dataSteward: String(userInputRow['Data Steward'] || '').trim(),
    platform: String(userInputRow['Platform *'] || userInputRow['Platform'] || '').trim(),
    schedulerName: String(userInputRow['Scheduler Name *'] || userInputRow['Scheduler Name'] || '').trim(),
    jobName: String(userInputRow['Job Name *'] || userInputRow['Job Name'] || '').trim(),
    scheduleDays: String(userInputRow['Schedule Days *'] || userInputRow['Schedule Days'] || '').trim(),
    executionStartTime: excelTimeToHHMM(userInputRow['Execution Start Time *'] || userInputRow['Execution Start Time'] || '').trim(),
    executionEndTime: excelTimeToHHMM(userInputRow['Execution End Time'] || '').trim(),
    averageExecutionTime: String(userInputRow['Average Execution Time'] || userInputRow['Avg Execution Time (min)'] || '').trim(),
    loadFrequency: String(userInputRow['Load Frequency'] || '').trim(),
    slaType: String(userInputRow['SLA Type *'] || userInputRow['SLA Type'] || '').trim(),
    slaDeadlineType: String(userInputRow['SLA Deadline Type'] || '').trim(),
    slaDays: String(userInputRow['SLA Days *'] || userInputRow['SLA Days'] || '').trim(),
    slaStartTime: excelTimeToHHMM(userInputRow['SLA Start Time *'] || userInputRow['SLA Start Time (CST) *'] || userInputRow['SLA Start Time (CST)'] || userInputRow['SLA Start Time'] || '').trim(),
    slaEndTime: excelTimeToHHMM(userInputRow['SLA End Time *'] || userInputRow['SLA End Time (CST) *'] || userInputRow['SLA End Time (CST)'] || userInputRow['SLA End Time'] || '').trim(),
    slaCriteria: String(userInputRow['SLA Criteria *'] || userInputRow['SLA Criteria'] || '').trim(),
    slaEndDayOffset: String(userInputRow['SLA End Day Offset'] || '').trim(),
    slaCriticality: String(userInputRow['SLA Criticality'] || '').trim(),
    slaMaxRuntimeMinutes: String(userInputRow['SLA Max Runtime (min)'] || '').trim(),
    slaScheduleTrigger: String(userInputRow['SLA Schedule Trigger'] || '').trim(),
    remarks: remarksFromUser,
    // Extended columns (Z-CC)
    forumsOrSystems: String(userInputRow['Forums Or Systems'] || '').trim(),
    inScope: String(userInputRow['In Scope'] || '').trim(),
    jobType: String(userInputRow['Job Type'] || '').trim(),
    release: String(userInputRow['Release'] || '').trim(),
    area: String(userInputRow['Area'] || '').trim(),
    pushPullMechanism: String(userInputRow['Push Pull Mechanism'] || '').trim(),
    timeLapseBetweenSourceUpdateAndLoadInLanding: String(userInputRow['Time Lapse Between Source Update And Load In Landing'] || '').trim(),
    dependenciesWithOtherJobsReferSequence: String(userInputRow['Dependencies With Other Jobs Refer Sequence'] || '').trim(),
    gdoDependencies: String(userInputRow['Gdo Dependencies'] || '').trim(),
    jobOwner: String(userInputRow['Job Owner'] || '').trim(),
    dataSourceOfExtraction: String(userInputRow['Data Source Of Extraction'] || '').trim(),
    sourceFilesTablesViews: String(userInputRow['Source - Files/tables/views'] || '').trim(),
    datasetType: String(userInputRow['Dataset Type'] || '').trim(),
    dataLoadingStrategy: String(userInputRow['Data Loading Strategy'] || '').trim(),
    originalSourceDataAvailabilityCst: String(userInputRow['Original Source Data Availability Cst'] || '').trim(),
    eventOrScheduled: String(userInputRow['Event Or Scheduled'] || '').trim(),
    errorHandlingAction: String(userInputRow['Error Handling Action'] || '').trim(),
    jobEndTimeCst: String(userInputRow['Job End Time Cst'] || '').trim(),
    jobEndTimeUtc: String(userInputRow['Job End Time Utc'] || '').trim(),
    typeOfTransferJob: String(userInputRow['Type Of Transfer Job'] || '').trim(),
    hardGdoDependencies: String(userInputRow['Hard Gdo Dependencies'] || '').trim(),
    expectedExecutionWindows: String(userInputRow['Expected Execution Windows'] || '').trim(),
    o9ErrorHandling: String(userInputRow['O9 Error Handling'] || '').trim(),
    pipelineName: String(userInputRow['Pipeline Name'] || '').trim(),
    executionDate: String(userInputRow['Execution Date'] || '').trim(),
    workingDayToBeExecuted: String(userInputRow['Working Day To Be Executed'] || '').trim(),
    jobGroup: String(userInputRow['Job Group'] || '').trim(),
    batchScope: String(userInputRow['Batch Scope'] || '').trim(),
    jobStartTimeCetCest: String(userInputRow['Job Start Time Cet Cest'] || '').trim(),
    jobEndTimeCetCest: String(userInputRow['Job End Time Cet Cest'] || '').trim(),
    op: String(userInputRow['Op'] || '').trim(),
    dependancyWithEvent: String(userInputRow['Dependancy With Event'] || '').trim(),
    dependencyOnForun: String(userInputRow['Dependency On Forun'] || '').trim(),
    dateOfExecution: String(userInputRow['Date Of Execution'] || '').trim(),
    month: String(userInputRow['Month'] || '').trim(),
    firstDayOfMonth: String(userInputRow['1st Day Of Month'] || '').trim(),
    dmrDate: String(userInputRow['Dmr Date'] || '').trim(),
    smrDate: String(userInputRow['Smr Date'] || '').trim(),
    workingDay: String(userInputRow['Working Day'] || '').trim(),
    calenderDay: String(userInputRow['Calender Day'] || '').trim(),
    fridayOfSmr: String(userInputRow['Friday Of Smr'] || '').trim(),
    mondayOfDmr: String(userInputRow['Monday Of Dmr'] || '').trim(),
    forumDependency: String(userInputRow['Forum Dependency'] || '').trim(),
    forumDependencyWorkingDay: String(userInputRow['Forum Dependency Working Day'] || '').trim(),
    previousJob: String(userInputRow['Previous Job'] || '').trim(),
    dataProviderForManualFiles: String(userInputRow['Data Provider For Manual Files'] || '').trim(),
    itSourceContact: String(userInputRow['It Source Contact'] || '').trim(),
    originalExecutionDate: String(userInputRow['Original Execution Date'] || '').trim(),
    updatedDate: String(userInputRow['Updated Date'] || '').trim(),
    transferDuration: String(userInputRow['Transfer Duration'] || '').trim(),
    mdtStartTime: String(userInputRow['Mdt Start Time'] || '').trim(),
    jobNotificationMethod: String(userInputRow['Job Notification Method'] || '').trim(),
    batchLoadStartTime: String(userInputRow['Batch Load Start Time'] || '').trim(),
    sourceOwnerContact: String(userInputRow['Source Owner Contact'] || '').trim(),
    factoryName: String(userInputRow['ADF Factory Name'] || '').trim(),
    workspaceName: String(userInputRow['Workspace Name'] || '').trim(),
    application: String(userInputRow['Application'] || '').trim(),
    folderPath: String(userInputRow['Folder Path'] || '').trim(),
    triggerName: String(userInputRow['Trigger Name'] || '').trim(),
  }});
}

export function parseXLSXFile(file: File): Promise<Record<string, any>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const XLSX = require('xlsx');
        const workbook = XLSX.read(data, { type: 'array' });
        // Find the GDO data sheet by name instead of relying on sheet position
        const sheetName = workbook.SheetNames.find(
          (name: string) => name.trim().toLowerCase() === 'gdo data'
        ) || workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 1 });

        // Row 1 (index 0): Title banner
        // Row 2 (index 1): Empty
        // Row 3 (index 2): Headers with comments
        // Row 4+ (index 3): Data rows
        const dataRows = (jsonData as string[][]).slice(3);

        if (dataRows.length === 0) {
          resolve([]);
          return;
        }

        const headers = (jsonData as string[][])[2] || [];
        const headerError = validateFileHeaders(headers);
        if (headerError) {
          reject(new Error(headerError));
          return;
        }

        const result = dataRows.map((row: string[], idx: number) => {
          const obj: Record<string, any> = {};
          headers.forEach((h, i) => {
            obj[h] = row[i] || '';
          });
          return obj;
        });
        resolve(result);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

export function validateFile(file: File): string | null {
  if (!file.name.endsWith('.xlsx')) {
    return 'Only .xlsx files are accepted. Please download the template and upload the .xlsx file.';
  }
  if (file.size > 10 * 1024 * 1024) {
    return 'File size exceeds the 10 MB limit. Please reduce the file size and retry.';
  }
  if (file.size === 0) {
    return 'Uploaded file is empty. Please provide a valid file.';
  }
  return null;
}
